<template>
  <div>
    <div class="page-header"><h2><span class="breadcrumb-level">数据统计</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">分析报告</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">订单分析</span></h2></div>

    <!-- 总体指标 -->
    <el-row :gutter="20" class="metrics-row">
      <el-col :span="6"><el-card shadow="hover" class="metric-card">
        <div class="metric-value">{{ data.total }}</div>
        <div class="metric-label">总订单</div>
      </el-card></el-col>
      <el-col :span="6"><el-card shadow="hover" class="metric-card">
        <div class="metric-value">{{ data.today }}</div>
        <div class="metric-label">今日订单</div>
      </el-card></el-col>
      <el-col :span="6"><el-card shadow="hover" class="metric-card">
        <div class="metric-value">¥{{ formatMoney(data.total_revenue) }}</div>
        <div class="metric-label">总营收</div>
      </el-card></el-col>
      <el-col :span="6"><el-card shadow="hover" class="metric-card">
        <div class="metric-value">¥{{ formatMoney(data.today_revenue) }}</div>
        <div class="metric-label">今日营收</div>
      </el-card></el-col>
    </el-row>

    <!-- 各类型订单详情 -->
    <div class="type-section" v-for="t in typeList" :key="t.label">
      <h3 class="type-section-title">{{ t.label }}</h3>
      <el-row :gutter="20" style="margin-bottom:16px">
        <el-col :span="6"><el-card shadow="hover" class="metric-card">
          <div class="metric-value">{{ t.count }}</div>
          <div class="metric-label">总{{ t.label }}订单</div>
        </el-card></el-col>
        <el-col :span="6"><el-card shadow="hover" class="metric-card">
          <div class="metric-value">{{ t.today }}</div>
          <div class="metric-label">今日{{ t.label }}订单</div>
        </el-card></el-col>
        <el-col :span="6"><el-card shadow="hover" class="metric-card">
          <div class="metric-value">¥{{ formatMoney(t.amount) }}</div>
          <div class="metric-label">{{ t.label }}总营收</div>
        </el-card></el-col>
        <el-col :span="6"><el-card shadow="hover" class="metric-card">
          <div class="metric-value">¥{{ formatMoney(t.today_amount) }}</div>
          <div class="metric-label">{{ t.label }}今日营收</div>
        </el-card></el-col>
      </el-row>
      <el-row :gutter="20" style="margin-bottom:24px">
        <el-col :span="12">
          <el-card shadow="hover"><template #header>{{ t.label }}订单状态</template>
            <div class="pie-list">
              <div class="pie-item" v-for="s in t.status" :key="s.label">
                <span class="pie-dot" :style="{ background: colors[statusIdx(t, s)] }"></span>
                <span class="pie-label">{{ s.label }}</span>
                <span class="pie-bar-bg"><span class="pie-bar-fill" :style="{ width: pctType(t, s)+'%', background: colors[statusIdx(t, s)] }"></span></span>
                <span class="pie-pct">{{ pctType(t, s) }}%</span>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="12">
          <el-card shadow="hover"><template #header>{{ t.label }}近7天营收</template>
            <div class="bar-chart-wrap"><div class="bar-chart">
              <div class="bar-item" v-for="(item, i) in t.trend" :key="i">
                <div class="bar-bar" :style="{ height: barHT(item.trend, i)+'%' }">
                  <span class="bar-num">¥{{ item.amount }}</span>
                </div>
                <div class="bar-label">{{ item.date.slice(5) }}</div>
              </div>
            </div></div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 总体营收趋势 -->
    <el-card shadow="hover">
      <template #header>全部订单近7天营收趋势</template>
      <div class="bar-chart-wrap"><div class="bar-chart">
        <div class="bar-item" v-for="(item, i) in raw.amount_trends" :key="i">
          <div class="bar-bar" :style="{ height: barH(item.amount)+'%' }">
            <span class="bar-num">¥{{ item.amount }}</span>
          </div>
          <div class="bar-label">{{ item.date.slice(5) }}</div>
        </div>
      </div></div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import * as api from '../../api/statistics';

const raw = ref({});
const data = computed(() => raw.value.stats || {});
const colors = ['#409eff', '#67c23a', '#e6a23c', '#f56c6c', '#909399', '#9b59b6'];
const typeList = computed(() => (raw.value.by_type || []).map(t => ({
  ...t,
  today_amount: t.trend && t.trend.length > 0 ? t.trend[t.trend.length - 1].amount : 0,
})));

const pctType = (type, s) => {
  const arr = Array.isArray(type.status) ? type.status : [];
  const total = arr.reduce((a, b) => a + (b.count || 0), 0) || 1;
  return ((s.count / total) * 100).toFixed(1);
};
const statusIdx = (type, s) => type.status.indexOf(s);

const formatMoney = (v) => {
  if (!v && v !== 0) return '0';
  return Number(v).toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};
const barH = (count) => {
  const arr = raw.value.amount_trends || [];
  const max = Math.max(...arr.map(t => t.amount || 0), 1);
  return Math.max(4, (count / max) * 100);
};
const barHT = (trend, idx) => {
  const max = Math.max(...trend.map(t => t.amount || 0), 1);
  return Math.max(4, ((trend[idx]?.amount || 0) / max) * 100);
};

onMounted(async () => { try { const r = await api.getOrderStats(); raw.value = r.data || {}; } catch {} });
</script>

<style scoped>
.metrics-row { margin-bottom: 24px; }
.metric-card { text-align: center; padding: 10px 0; }
.metric-value { font-size: 26px; font-weight: 700; color: #409eff; line-height: 1.2; }
.metric-label { font-size: 13px; color: #606266; margin-top: 6px; }
.type-section { margin-bottom: 32px; }
.type-section-title { font-size: 18px; font-weight: 600; color: #303133; margin-bottom: 16px; padding-left: 12px; border-left: 4px solid #409eff; }
.bar-chart-wrap { padding: 10px 0; }
.bar-chart { display: flex; align-items: flex-end; justify-content: space-around; height: 180px; }
.bar-item { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; }
.bar-bar { width: 36px; background: linear-gradient(180deg, #67c23a, #95d475); border-radius: 4px 4px 2px 2px; min-height: 6px; display: flex; align-items: flex-start; justify-content: center; padding-top: 3px; transition: height .5s; }
.bar-num { font-size: 9px; color: #fff; font-weight: 600; }
.bar-label { font-size: 11px; color: #909399; margin-top: 6px; }
.pie-list { padding: 4px 10px; }
.pie-item { display: flex; align-items: center; padding: 8px 0; border-bottom: 1px solid #f5f5f5; gap: 8px; }
.pie-item:last-child { border-bottom: none; }
.pie-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.pie-label { width: 60px; font-size: 13px; color: #606266; }
.pie-bar-bg { flex: 1; height: 8px; background: #f0f0f0; border-radius: 4px; overflow: hidden; }
.pie-bar-fill { display: block; height: 100%; border-radius: 4px; transition: width .6s; }
.pie-pct { width: 40px; text-align: right; font-size: 12px; color: #909399; }
</style>
